import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toISODate } from "@/lib/format";

export default function TransactionDialog({ open, onOpenChange, onSaved, initial, defaultStatus = "completed" }) {
  const [form, setForm] = useState({
    type: "income",
    amount: "",
    currency: "TRY",
    category_id: "",
    description: "",
    date: toISODate(new Date()),
    status: defaultStatus,
    counterparty: "",
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
      if (initial) {
        setForm({
          type: initial.type,
          amount: String(initial.amount),
          currency: initial.currency,
          category_id: initial.category_id || "",
          description: initial.description || "",
          date: initial.date,
          status: initial.status,
          counterparty: initial.counterparty || "",
        });
      } else {
        setForm((f) => ({ ...f, status: defaultStatus, date: toISODate(new Date()) }));
      }
    }
  }, [open, initial, defaultStatus]);

  const update = (k) => (v) => setForm({ ...form, [k]: typeof v === "object" && v?.target ? v.target.value : v });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Tutar 0'dan büyük olmalı");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        category_id: form.category_id || null,
      };
      if (initial?.id) {
        await api.put(`/transactions/${initial.id}`, payload);
        toast.success("İşlem güncellendi");
      } else {
        await api.post("/transactions", payload);
        toast.success("İşlem eklendi");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredCats = categories.filter((c) => c.kind === form.type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131924] border-white/10 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {initial ? "İşlemi Düzenle" : "Yeni İşlem"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Gelir, gider, tahsilat veya ödeme kaydı oluşturun.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category_id: "" })}>
                <SelectTrigger data-testid="tx-type-select" className="mt-2 bg-[#0F1522] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                  <SelectItem value="income">Gelir</SelectItem>
                  <SelectItem value="expense">Gider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="tx-status-select" className="mt-2 bg-[#0F1522] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                  <SelectItem value="completed">Gerçekleşen</SelectItem>
                  <SelectItem value="pending">Yaklaşan (Bekleyen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tutar</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={update("amount")}
                data-testid="tx-amount-input"
                className="mt-2 bg-[#0F1522] border-white/10 font-mono-num"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="tx-currency-select" className="mt-2 bg-[#0F1522] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                  <SelectItem value="TRY">₺ TRY</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tarih</Label>
              <Input
                type="date"
                value={form.date}
                onChange={update("date")}
                data-testid="tx-date-input"
                className="mt-2 bg-[#0F1522] border-white/10"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Kategori</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="tx-category-select" className="mt-2 bg-[#0F1522] border-white/10">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-white/10 text-slate-100 max-h-60">
                  {filteredCats.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Müşteri / Karşı Taraf</Label>
            <Input
              value={form.counterparty}
              onChange={update("counterparty")}
              data-testid="tx-counterparty-input"
              className="mt-2 bg-[#0F1522] border-white/10"
              placeholder="Örn: ABC Danışmanlık"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Açıklama</Label>
            <Textarea
              value={form.description}
              onChange={update("description")}
              data-testid="tx-description-input"
              className="mt-2 bg-[#0F1522] border-white/10"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-100">
              İptal
            </Button>
            <Button type="submit" disabled={saving} data-testid="tx-save-button" className="bg-indigo-500 hover:bg-indigo-600">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
